import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLogout, useMe } from '../lib/auth';
import { IconGear } from './icons';

function initials(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

/**
 * Avatar + username trigger opening a token-styled popover (REV20 — docs/specs/REV20-hexa-page.md):
 * a Settings link (replacing `/settings`'s old spot in the nav — see `app/navigation.ts`) plus the
 * existing Log out. Escape and click-outside close it; the admin chip stays visible on the trigger
 * itself, same as before, so it's never hidden behind an extra click.
 */
export function UserMenu() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    // Capture phase, same reasoning as CommandPalette's own Escape listener: this popover can be
    // open at the same time as other window-level listeners, and click-outside needs to see the
    // event before anything else potentially stops it.
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  if (!me) {
    return null;
  }

  return (
    <div className="relative" ref={containerRef} data-testid="user-menu">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-tap items-center gap-2 rounded-lg px-1.5 transition-colors hover:bg-bg-elevated-hover"
      >
        {me.avatarUrl ? (
          <img
            src={me.avatarUrl}
            alt=""
            data-testid="user-avatar"
            width={32}
            height={32}
            className="size-8 rounded-full border border-border-strong"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex size-8 items-center justify-center rounded-full border border-border-strong bg-bg-elevated-hover font-display text-xs font-semibold text-text-muted"
          >
            {initials(me.username)}
          </span>
        )}
        <span className="hidden max-w-32 truncate text-sm font-semibold text-text-muted sm:inline">
          {me.username}
        </span>
        {me.role === 'ADMIN' && (
          <span className="hidden rounded bg-accent-dim px-1.5 py-0.5 font-display text-[0.625rem] font-semibold uppercase tracking-wider text-accent-strong sm:inline">
            Admin
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="user-menu-popover"
          className="absolute right-0 top-full z-30 mt-2 w-44 overflow-hidden rounded-xl border border-border-strong bg-bg-elevated shadow-2xl"
        >
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="flex min-h-tap items-center gap-2 px-4 text-sm font-semibold text-text-muted transition-colors hover:bg-bg-elevated-hover hover:text-text"
          >
            <IconGear aria-hidden="true" />
            Settings
          </Link>
          <button
            type="button"
            disabled={logout.isPending}
            onClick={() => {
              setOpen(false);
              logout.mutate(undefined, {
                onSettled: () => navigate('/login', { replace: true }),
              });
            }}
            className="flex min-h-tap w-full items-center px-4 text-left text-sm font-semibold text-text-faint transition-colors hover:bg-bg-elevated-hover hover:text-text disabled:opacity-50"
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
